import SearchClient from "@/components/search/searchClient";
import Hero from "@/components/hero";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import BreadcrumbJsonLd from "@/components/common/breadcrumbJsonLd";
import { getJobs } from "@/lib/api";
import type { PaginatedJobs } from "@/types/db";

interface SearchParams {
  q?: string;
  country?: string;
  state?: string;
  /** Comma-separated slugs (`"python-data-jobs,senior-data-jobs"`). Stored
   * as a string so TanStack Router emits `?tags=a,b` instead of JSON-encoded
   * array form — pasted/shared URLs stay clean and human-readable. */
  tags?: string;
}

import { splitTags } from "@/lib/tagFilterUrl";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");

export const Route = createFileRoute("/search")({
  head: () => ({
    meta: [
      { title: "Search Data Jobs | Data First Jobs" },
      {
        name: "description",
        content:
          "Search remote data engineering, analytics, and data science jobs. Filter by skills, cloud stack, seniority, and more.",
      },
      {
        property: "og:title",
        content: "Search Data Jobs | Data First Jobs",
      },
      {
        property: "og:description",
        content:
          "Search remote data engineering, analytics, and data science jobs.",
      },
      { property: "og:url", content: `${SITE_URL}/search` },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/search` }],
  }),
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: typeof search.q === "string" ? search.q : undefined,
    country: typeof search.country === "string" ? search.country : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
    tags: typeof search.tags === "string" ? search.tags : undefined,
  }),
  loaderDeps: ({ search: { q, country, state, tags } }) => ({
    q,
    country,
    state,
    tags,
  }),
  loader: async ({ deps: { q, country, state, tags } }) => {
    const tagSlugs = splitTags(tags);
    const initialData = (await getJobs({
      data: {
        limit: 20,
        search: q,
        country,
        state,
        ...(tagSlugs.length > 0 ? { tags: tagSlugs } : {}),
      },
    })) as PaginatedJobs;
    return {
      initialJobs: initialData.jobs,
      initialTotal: initialData.total,
      q,
      country,
      state,
      tagSlugs,
    };
  },
  component: SearchPage,
});

function SearchPage() {
  const { initialJobs, initialTotal, q, country, state, tagSlugs } =
    Route.useLoaderData();
  const { jobCategoriesWithTags, locations } = rootRoute.useRouteContext();

  return (
    <>
      <BreadcrumbJsonLd
        items={[{ name: "Home", url: SITE_URL }, { name: "Search" }]}
      />
      <Hero
        title={
          <>
            Find your <span className="text-polaris-orange">next move</span> in
            data
          </>
        }
        subtitle="Tagged by stack, seniority, and remote, so the right role is easier to spot."
        hideBrowseLink
        hideEmailForm
        primaryCta={{
          label: `Browse ${initialTotal.toLocaleString()} jobs`,
          href: "#jobList",
        }}
      />
      <SearchClient
        jobCategoriesWithTags={jobCategoriesWithTags}
        locations={locations}
        initialJobs={initialJobs}
        initialTotal={initialTotal}
        initialSearch={q}
        initialCountry={country}
        initialState={state}
        initialTagSlugs={tagSlugs}
      />
    </>
  );
}
